import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { FileUploadService } from '../../services/file-upload.service';
import { AuthService } from '../../../auth/services/auth.service';
import { FileOperation } from '../../models/file-operation';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { FileValidationService } from '../../../../shared/services/file-validation/file-validation.service';

@Component({
  selector: 'app-document-upload',
  standalone: true,
  imports: [FormsModule, SvgIconComponent],
  templateUrl: './document-upload.component.html',
  styleUrl: './document-upload.component.scss',
})
export class DocumentUploadComponent extends DestroyBase {
  fileValidationService = inject(FileValidationService);
  file: File | null = null;
  isValidFile = false;
  validationErrors: string[] = [];
  isUploadSuccess: boolean | null = null;
  fileId: number | undefined;
  uploadingFile = false;
  isDragOver = false;
  selectedOperationIds: number[] = [];
  hasConsentChecked = false;
  hasTermsChecked = false;

  readonly PORTFOLIO_MINING_OP = 'Portfolio Mining';

  readonly allowedExtensions = [
    'doc',
    'xlsx',
    'xls',
    'odt',
    'pdf',
    'txt',
    'docx',
    'zip',
    'csv',
    'xlsm',
  ];

  @Input() fileOperations: FileOperation[] = [];
  @Output() fileUploaded = new EventEmitter<void>();

  @ViewChild('inputFile', { static: true }) inputFile!: ElementRef<HTMLElement>;

  fileUploadService = inject(FileUploadService);
  authService = inject(AuthService);
  auditLogService = inject(AuditLogService);

  uploadFileClicked(): void {
    const el: HTMLElement = this.inputFile.nativeElement;
    el.click();
  }

  removeFile(): void {
    this.file = null;
    this.isValidFile = false;
    this.hasConsentChecked = false;
    this.hasTermsChecked = false;
    this.selectedOperationIds = [];
  }

  onFileSelect(event: Event): void {
    this.file = null;
    this.isUploadSuccess = null;
    this.validationErrors = [];
    this.hasConsentChecked = false;
    this.hasTermsChecked = false;
    this.selectedOperationIds = [];
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const selectedFile = input.files[0];
      this.handleFile(selectedFile);
    }

    input.value = '';
  }

  async handleFile(selectedFile: File): Promise<void> {
    this.file = selectedFile;

    const isValid = await this.fileValidationService.validateFile(selectedFile);

    this.validationErrors = this.fileValidationService.validationErrors;

    if (isValid) {
      this.isValidFile = true;
      this.selectedOperationIds = this.fileOperations
        .filter(
          (op) =>
            op.operation_name !== this.PORTFOLIO_MINING_OP &&
            this.isExcelOrCsv(),
        )
        .map((op) => op.operation_id);
    } else {
      this.isValidFile = false;
    }
  }

  isExcelOrCsv(): boolean {
    return (
      this.file?.name.endsWith('.xlsx') ||
      this.file?.name.endsWith('.xls') ||
      this.file?.name.endsWith('.xlsm') ||
      this.file?.name.endsWith('.csv') ||
      false
    );
  }

  // Drag Over event
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  // Drag Leave event
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  // Drop event
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    this.file = null;
    this.isUploadSuccess = null;
    this.validationErrors = [];
    this.hasConsentChecked = false;
    this.hasTermsChecked = false;
    this.selectedOperationIds = [];

    const files = event.dataTransfer?.files;
    const input = event.target as HTMLInputElement;
    if (files && files.length > 0) {
      const droppedFile = files[0];
      this.handleFile(droppedFile);
    }
    input.value = '';
  }

  uploadFileToAzure(): void {
    if (!this.file) return;

    this.auditLogService.logFeature({
      featureName: 'new_file_upload',
      action: 'upload',
      meta: {
        fileName: this.file.name,
        fileSizeMB: String(Math.round(this.file.size / 1024 / 1024)),
      },
      description: 'User uploads a new document',
    });

    this.uploadingFile = true;

    this.fileUploadService
      .uploadFile(this.file, this.selectedOperationIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.uploadDetails(true);
          this.removeFile();
          this.fileUploaded.emit();
        },
        error: (err) => {
          console.error('File upload initiation failed:', err);
          this.uploadDetails(false);
          const nonFieldError = err.error?.non_field_errors?.[0];
          if (nonFieldError && nonFieldError.includes('Missing columns:')) {
            const columnsString = nonFieldError
              .split('Missing columns:')[1]
              .trim();
            this.validationErrors.push('Missing columns:');
            const missingColumns = columnsString
              .split(',')
              .map((col: string) => col.trim());
            missingColumns.forEach((column: string) => {
              this.validationErrors.push(column);
            });
            this.isValidFile = false;
          } else if (nonFieldError) {
            this.validationErrors.push(nonFieldError);
            this.isValidFile = false;
          } else if (err.error?.file_name) {
            this.validationErrors.push(err.error.file_name[0]);
            this.isValidFile = false;
          } else {
            this.validationErrors.push('An error occurred. Please try again.');
            this.isValidFile = false;
          }
        },
      });
  }

  onOperationChange(event: Event, operation_id: number): void {
    const checkbox = event.target as HTMLInputElement;
    if (checkbox.checked) {
      this.selectedOperationIds.push(operation_id);
    } else {
      this.selectedOperationIds = this.selectedOperationIds.filter(
        (id) => id !== operation_id,
      );
    }
  }

  uploadDetails(isUploadSuccess: boolean): void {
    this.uploadingFile = false;
    this.isUploadSuccess = isUploadSuccess;
  }

  deleteFile(): void {
    this.fileUploadService
      .deleteFile(this.fileId!)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }
  get visibleOperations(): FileOperation[] {
    return this.fileOperations.filter(
      (op) =>
        op.operation_name === this.PORTFOLIO_MINING_OP || this.isExcelOrCsv(),
    );
  }
}
